"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useState } from "react";

import { BlogNewsletterEmailCapture } from "@/components/blog/BlogNewsletterEmailCapture";

const KEY = "ed-blog-exit-intent-dismissed";

/**
 * Exit-intent strip + email capture (welcome sequence via weekly_ledger source tag).
 */
export function BlogExitIntentBanner() {
  const pathname = usePathname();
  const [visible, setVisible] = useState(false);

  const dismiss = useCallback(() => {
    setVisible(false);
    try {
      sessionStorage.setItem(KEY, "1");
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!pathname?.startsWith("/blog/")) return;
    try {
      if (sessionStorage.getItem(KEY)) return;
    } catch {
      /* ignore */
    }

    const onOut = (e: MouseEvent) => {
      const to = e.relatedTarget as Node | null;
      if (to && document.documentElement.contains(to)) return;
      if (e.clientY > 18) return;
      setVisible(true);
      document.removeEventListener("mouseout", onOut);
    };

    document.addEventListener("mouseout", onOut);
    return () => document.removeEventListener("mouseout", onOut);
  }, [pathname]);

  if (!visible) return null;

  return (
    <div className="ed-blog-exit-intent" role="dialog" aria-label="Newsletter and product">
      <div className="ed-blog-exit-intent__inner ed-blog-exit-intent__inner--stack">
        <p className="ed-blog-exit-intent__text">
          <strong>Before you go</strong> — bring dated stock and pill counts into one workflow. No new spreadsheet tab required.
        </p>
        <BlogNewsletterEmailCapture source="blog-exit-intent" variant="exit" />
        <div className="ed-blog-exit-intent__actions">
          <Link href="/signup" className="ed-blog-exit-intent__primary" onClick={dismiss}>
            Try ExpiryDesk free
          </Link>
          <button type="button" className="ed-blog-exit-intent__dismiss" onClick={dismiss}>
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
